<template>
  <section
    class="pdf-learning-viewer"
    :class="{ fullscreen: isFullscreen, mobile: isMobile, 'readonly': readOnly }"
    dir="ltr"
    @contextmenu.prevent
  >
    <header class="pdf-toolbar" :class="{ collapsed: mobileToolbarCollapsed }">
      <div class="toolbar-row main-row">
        <button class="icon-btn mobile-only" type="button" @click="mobileToolbarCollapsed = !mobileToolbarCollapsed">
          <i class="pi pi-bars"></i>
        </button>
        <button class="icon-btn" type="button" @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1">
          <i class="pi pi-chevron-left"></i>
        </button>
        <label class="page-control">
          <input v-model.number="pageInput" type="number" min="1" :max="totalPages" @change="goToPage(pageInput)" />
          <span>/ {{ totalPages || '-' }}</span>
        </label>
        <button class="icon-btn" type="button" @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages">
          <i class="pi pi-chevron-right"></i>
        </button>
        <div class="toolbar-spacer"></div>
        <span class="save-state" :class="saveState">{{ saveText }}</span>
        <button class="icon-btn" type="button" @click="toggleFullscreen">
          <i :class="['pi', isFullscreen ? 'pi-window-minimize' : 'pi-window-maximize']"></i>
        </button>
      </div>

      <div class="toolbar-row secondary-row">
        <button class="icon-btn" type="button" @click="setZoom(zoom - 0.15)"><i class="pi pi-search-minus"></i></button>
        <span class="zoom-label">{{ Math.round(zoom * 100) }}%</span>
        <button class="icon-btn" type="button" @click="setZoom(zoom + 0.15)"><i class="pi pi-search-plus"></i></button>
        <button class="text-btn" type="button" @click="fitWidth">Fit Width</button>
        <button class="text-btn" type="button" @click="fitPage">Fit Page</button>
        <button class="icon-btn" type="button" @click="rotatePage"><i class="pi pi-refresh"></i></button>
        <div class="search-box">
          <i class="pi pi-search"></i>
          <input v-model.trim="searchQuery" type="search" placeholder="Search" @keyup.enter="runSearch" />
          <button class="mini-btn" type="button" @click="runSearch">Go</button>
          <span v-if="searchResults.length">{{ activeSearchIndex + 1 }}/{{ searchResults.length }}</span>
        </div>
      </div>
    </header>

    <div class="pdf-body">
      <aside class="thumbs-panel" :class="{ open: thumbnailsOpen }">
        <button class="thumb-toggle mobile-only" type="button" @click="thumbnailsOpen = false">
          <i class="pi pi-times"></i>
        </button>
        <div
          v-for="page in pageNumbers"
          :key="'thumb-' + page"
          class="thumb"
          :class="{ active: page === currentPage }"
          :data-thumb-page="page"
          @click="goToPage(page)"
        >
          <canvas :ref="el => setThumbRef(page, el)"></canvas>
          <span>{{ page }}</span>
        </div>
      </aside>

      <main
        ref="scrollArea"
        class="pdf-scroll"
        @scroll="onScroll"
        @wheel="onWheel"
        @touchstart.passive="onTouchStart"
        @touchend.passive="onTouchEnd"
      >
        <button class="floating-thumbs mobile-only" type="button" @click="thumbnailsOpen = true">
          <i class="pi pi-images"></i>
        </button>
        <div v-if="loading" class="viewer-loading">
          <span class="loader-dot"></span>
          Loading PDF...
        </div>
        <div v-if="error" class="viewer-error">{{ error }}</div>

        <div class="virtual-spacer" :style="{ height: topSpacerHeight + 'px' }"></div>
        <div
          v-for="page in visiblePageNumbers"
          :key="'page-' + page"
          class="page-shell"
          :data-page="page"
          :style="{ height: pageHeights[page] + 'px' }"
        >
          <div
            class="page-frame"
            :style="pageFrameStyle(page)"
          >
            <canvas :ref="el => setCanvasRef(page, el)" class="pdf-canvas"></canvas>
            <svg
              class="annotation-layer"
              :viewBox="'0 0 1 1'"
              preserveAspectRatio="none"
              @pointerdown="onAnnotationPointerDown($event, page)"
              @pointermove="onAnnotationPointerMove"
              @pointerup="onAnnotationPointerUp"
              @pointerleave="onAnnotationPointerUp"
            >
              <g v-if="watermarkEnabled && watermarkDisplayText" class="watermark-layer">
                <template v-for="row in watermarkGrid" :key="'wm-row-' + row.y">
                  <text
                    v-for="col in row.cols"
                    :key="'wm-' + row.y + '-' + col.x"
                    :x="col.x"
                    :y="row.y"
                    transform="rotate(-28)"
                    fill="currentColor"
                  >{{ watermarkDisplayText }}</text>
                </template>
              </g>
              <rect
                v-for="(hit, index) in searchHighlights[page] || []"
                :key="'hit-' + page + '-' + index"
                class="search-highlight"
                :x="hit.x"
                :y="hit.y"
                :width="hit.width"
                :height="hit.height"
              />
              <g v-for="item in annotationsByPage[page] || []" :key="item.id" :class="'annotation-' + item.type">
                <polyline
                  v-if="item.type === 'pen' || item.type === 'highlighter'"
                  :points="pointsToString(item.points)"
                  :stroke="item.color"
                  :stroke-width="item.width"
                  :opacity="item.opacity"
                  fill="none"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  vector-effect="non-scaling-stroke"
                />
                <rect
                  v-else-if="item.type === 'rectangle'"
                  :x="rectFromItem(item).x"
                  :y="rectFromItem(item).y"
                  :width="rectFromItem(item).width"
                  :height="rectFromItem(item).height"
                  :stroke="item.color"
                  stroke-width="2"
                  fill="transparent"
                  vector-effect="non-scaling-stroke"
                />
                <ellipse
                  v-else-if="item.type === 'circle'"
                  :cx="ellipseFromItem(item).cx"
                  :cy="ellipseFromItem(item).cy"
                  :rx="ellipseFromItem(item).rx"
                  :ry="ellipseFromItem(item).ry"
                  :stroke="item.color"
                  stroke-width="2"
                  fill="transparent"
                  vector-effect="non-scaling-stroke"
                />
                <g v-else-if="item.type === 'arrow'">
                  <line
                    :x1="item.start.x"
                    :y1="item.start.y"
                    :x2="item.end.x"
                    :y2="item.end.y"
                    :stroke="item.color"
                    stroke-width="2"
                    vector-effect="non-scaling-stroke"
                  />
                  <polygon :points="arrowHead(item)" :fill="item.color" />
                </g>
                <text
                  v-else-if="item.type === 'text'"
                  :x="item.x"
                  :y="item.y"
                  :fill="item.color"
                  :font-size="item.size"
                  font-family="Arial, sans-serif"
                >{{ item.text }}</text>
              </g>
            </svg>
            <div v-if="renderingPages[page]" class="page-loading">Rendering...</div>
          </div>
        </div>
        <div class="virtual-spacer" :style="{ height: bottomSpacerHeight + 'px' }"></div>
      </main>

      <aside v-if="!readOnly" class="annotation-tools" :class="{ open: toolsOpen }">
        <button class="tool-toggle mobile-only" type="button" @click="toolsOpen = !toolsOpen">
          <i class="pi pi-pencil"></i>
        </button>
        <div class="tool-list">
          <button v-for="tool in tools" :key="tool.name" type="button" :class="{ active: activeTool === tool.name }" @click="selectTool(tool.name)">
            <i :class="tool.icon"></i>
          </button>
          <input v-model="strokeColor" class="color-input" type="color" />
          <button type="button" @click="undo"><i class="pi pi-undo"></i></button>
          <button type="button" @click="redo"><i class="pi pi-refresh"></i></button>
          <button type="button" @click="clearPage"><i class="pi pi-trash"></i></button>
        </div>
      </aside>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { markRaw } from "vue";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import "pdfjs-dist/legacy/build/pdf.worker.entry";

// TEMP-PDF-DEBUG (remove after network investigation): monotonic id assigned to
// every getDocument() call so two PDFDocumentProxy instances for the same URL are
// instantly distinguishable in the console (id=1 vs id=2 == a duplicate document).
let _pdfDocSeq = 0;

export default {
  name: "PdfLearningViewer",
  props: {
    pdfUrl: { type: String, required: true },
    contentId: { type: [Number, String], required: true },
    readOnly: { type: Boolean, default: false },
    watermarkEnabled: { type: Boolean, default: false },
    watermarkLines: { type: Array, default: () => [] },
  },
  data() {
    return {
      pdf: null,
      totalPages: 0,
      currentPage: 1,
      pageInput: 1,
      zoom: 1,
      // Cached available viewer content width (px). Each page is fitted to this
      // width and its height derived from the page's OWN aspect ratio — never A4.
      // Measured once per load/zoom/resize (in recalculateHeights), never per scroll.
      contentWidth: 0,
      rotation: 0,
      pageSizes: {},
      pageHeights: {},
      visiblePages: new Set(),
      renderedPages: new Set(),
      renderingPages: {},
      pendingRenderPages: new Set(),
      renderEpoch: 0,
      pageCanvases: {},
      thumbCanvases: {},
      renderedThumbs: new Set(),
      annotationsByPage: {},
      undoStack: {},
      redoStack: {},
      dirtyPages: new Set(),
      activeTool: "pan",
      strokeColor: "#d93025",
      drawing: null,
      loading: true,
      error: "",
      isFullscreen: false,
      isMobile: window.innerWidth <= 768,
      mobileToolbarCollapsed: window.innerWidth <= 768,
      thumbnailsOpen: false,
      toolsOpen: false,
      searchQuery: "",
      searchResults: [],
      activeSearchIndex: -1,
      searchHighlights: {},
      saveState: "saved",
      // When the optional annotations/progress backend is unavailable (e.g. 404/405),
      // remote sync is switched off for the session and we persist on-device only.
      // This stops the failing requests from being retried on a loop.
      remoteSyncDisabled: false,
      autosaveTimer: null,
      progressTimer: null,
      observer: null,
      thumbObserver: null,
      scrollRestore: null,
      renderWindowStart: 1,
      renderWindowEnd: 1,
      renderBuffer: 4,
      progressDirty: false,
      loadingProgress: false,
      lastSavedProgress: null,
      touchStart: null,
      tools: [
        { name: "pan", icon: "pi pi-arrows-alt" },
        { name: "pen", icon: "pi pi-pencil" },
        { name: "highlighter", icon: "pi pi-sun" },
        { name: "eraser", icon: "pi pi-eraser" },
        { name: "text", icon: "pi pi-font" },
        { name: "rectangle", icon: "pi pi-stop" },
        { name: "circle", icon: "pi pi-circle" },
        { name: "arrow", icon: "pi pi-arrow-up-right" },
      ],
    };
  },
  computed: {
    pageNumbers() {
      return Array.from({ length: this.totalPages }, (_, index) => index + 1);
    },
    visiblePageNumbers() {
      const start = Math.max(1, this.renderWindowStart);
      const end = Math.min(this.totalPages, this.renderWindowEnd);
      return Array.from({ length: Math.max(0, end - start + 1) }, (_, index) => start + index);
    },
    topSpacerHeight() {
      return this.sumPageHeights(1, this.renderWindowStart - 1);
    },
    bottomSpacerHeight() {
      return this.sumPageHeights(this.renderWindowEnd + 1, this.totalPages);
    },
    saveText() {
      if (this.saveState === "saving") return "Saving";
      if (this.saveState === "offline") return "Offline saved";
      if (this.saveState === "error") return "Retrying";
      if (this.saveState === "local") return "Saved on device";
      return "Saved";
    },
    offlineKey() {
      return `pdf-reader-offline-${this.contentId}`;
    },
    watermarkDisplayText() {
      return this.watermarkLines.find((line) => !!line) || "";
    },
    watermarkGrid() {
      return [0.18, 0.42, 0.66, 0.9].map((y) => ({
        y,
        cols: [0.05, 0.38, 0.71].map((x) => ({ x })),
      }));
    },
  },
  watch: {
    pdfUrl() {
      this.resetViewer();
      this.loadPdf();
    },
    zoom() {
      this.renderEpoch += 1;
      this.recalculateHeights();
      if (!this.loadingProgress) this.markProgressDirty();
      this.rerenderVisiblePages();
    },
    rotation() {
      this.renderEpoch += 1;
      this.recalculateHeights();
      this.rerenderVisiblePages();
    },
    toolsOpen() {
      this.$nextTick(this.updateMobileFloatingLayout);
    },
  },
  mounted() {
    // Non-reactive map of in-flight pdf.js RenderTasks (keyed by page) so obsolete
    // renders can be cancelled. Kept off `data` to avoid proxying the task objects.
    this._renderTasks = {};
    this.loadPdf();
    this.autosaveTimer = setInterval(this.flushAnnotations, 4000);
    // Progress saving is driven SOLELY by the 3s debounce (queueProgressSave) plus
    // the beforeUnmount flush — no periodic poller, so no /progress requests fire
    // during continuous scrolling. (Annotation autosave above is unrelated.)
    this._resizeHandler = () => {
      this.isMobile = window.innerWidth <= 768;
      this.updateMobileFloatingLayout();
      this.recalculateHeights();
      this.rerenderVisiblePages();
    };
    this._keyHandler = this.onKeydown;
    this._fullscreenHandler = this.onFullscreenChange;
    window.addEventListener("resize", this._resizeHandler);
    window.visualViewport?.addEventListener("resize", this.updateMobileFloatingLayout);
    window.visualViewport?.addEventListener("scroll", this.updateMobileFloatingLayout);
    window.addEventListener("keydown", this._keyHandler);
    document.addEventListener("fullscreenchange", this._fullscreenHandler);
    window.addEventListener("online", this.flushOfflineQueue);
    window.addEventListener("beforeunload", this.saveLocalBackup);
    this.$nextTick(this.updateMobileFloatingLayout);
  },
  beforeUnmount() {
    this.flushAnnotations();
    this.saveProgress(true);
    this.exitFullscreenClass();
    clearInterval(this.autosaveTimer);
    clearInterval(this.progressTimer);
    clearTimeout(this._progressTimeout);
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    // Cancel any renders still in flight so they don't run after teardown.
    Object.values(this._renderTasks || {}).forEach((task) => {
      try { task.cancel(); } catch (e) { /* already settled */ }
    });
    window.removeEventListener("resize", this._resizeHandler);
    window.visualViewport?.removeEventListener("resize", this.updateMobileFloatingLayout);
    window.visualViewport?.removeEventListener("scroll", this.updateMobileFloatingLayout);
    window.removeEventListener("keydown", this._keyHandler);
    document.removeEventListener("fullscreenchange", this._fullscreenHandler);
    window.removeEventListener("online", this.flushOfflineQueue);
    window.removeEventListener("beforeunload", this.saveLocalBackup);
    if (this.observer) this.observer.disconnect();
    if (this.thumbObserver) this.thumbObserver.disconnect();
    this.destroyAllCanvases();
    // Destroy the PDF document / any in-flight loading task to free worker memory.
    this.cancelActiveLoad();
  },
  methods: {
    selectTool(toolName) {
      this.activeTool = toolName;
      if (this.isMobile && toolName !== "pan") this.toolsOpen = false;
    },
    updateMobileFloatingLayout() {
      if (!this.$el || window.innerWidth > 768) return;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const bottomNav = document.querySelector(".app-bnav");
      const navRect = bottomNav?.getBoundingClientRect();
      const navHeight = navRect ? Math.max(0, window.innerHeight - navRect.top) : 0;
      const safeBottom = Math.max(16, navHeight + 16);
      const maxSheetHeight = Math.max(144, Math.min(220, viewportHeight - safeBottom - 92));
      this.$el.style.setProperty("--pdf-mobile-safe-bottom", `${safeBottom}px`);
      this.$el.style.setProperty("--pdf-mobile-tool-sheet-max-height", `${maxSheetHeight}px`);
    },
    // TEMP-PERF (remove after PDF-load profiling): relative-timestamp logger so
    // the exact stage timings can be read from the Flutter WebView console.
    plog(stage) {
      const now = (typeof performance !== "undefined" && performance.now) ? performance.now() : Date.now();
      if (this._perfT0 == null) this._perfT0 = now;
      // eslint-disable-next-line no-console
      console.log(`[PDF-PERF] ${stage} @ +${(now - this._perfT0).toFixed(1)}ms`);
    },
    // TEMP-RENDER-DEBUG (remove after Android fast-scroll verification): compact
    // render-lifecycle trace. Emits [PDF-RENDER] lines for intersection / render
    // start-end / skip / pending / canvas create-remove / cleanup so the exact
    // sequence behind a blank page can be read from the device console.
    rlog(fields) {
      if (this._pdfRenderDebug === false) return;
      const t = (typeof performance !== "undefined" && performance.now) ? performance.now().toFixed(0) : Date.now();
      // eslint-disable-next-line no-console
      console.log(`[PDF-RENDER] t=${t} ` + Object.keys(fields).map((k) => `${k}=${fields[k]}`).join(" "));
    },
    // TEMP-PDF-DEBUG (remove after network investigation): traces the document +
    // page-access lifecycle behind the HTTP range requests. [PDF-DEBUG] lines expose
    // getDocument START/END (+url +doc id), every getPage caller, render START/END/
    // SKIP/AGAIN, and canvas destroy — so repeated page access / repeated documents
    // can be read straight from the console without a network trace.
    pdbg(msg) {
      const t = (typeof performance !== "undefined" && performance.now) ? performance.now().toFixed(0) : Date.now();
      // eslint-disable-next-line no-console
      console.log(`[PDF-DEBUG] t=${t} doc=${this._docId != null ? this._docId : "-"} ${msg}`);
    },
    async loadPdf() {
      this._perfT0 = null; this._firstPaintLogged = false; // TEMP-PERF
      this.plog("PDF_LOAD_START"); // TEMP-PERF
      const url = this.pdfUrl;
      // Never load an empty/placeholder URL — the parent renders this viewer before
      // the lesson content has resolved, so the first value can be "".
      if (!url) {
        this.loading = false;
        this.error = "";
        return;
      }
      // Single-flight: ignore a duplicate request for the URL that is already loading.
      if (this._loadingUrl === url) return;
      this._loadingUrl = url;
      // Cancel/destroy any previous document or still-running loading task first,
      // so there is only ever one getDocument() operation alive at a time.
      await this.cancelActiveLoad();
      // Per-load epoch: every background continuation (progress/annotations/remaining
      // sizes/scroll-restore) captures this token and bails if a newer load starts or
      // the component unmounts. Reset the "user has taken over" guard for this load.
      this._loadToken = (this._loadToken || 0) + 1;
      const token = this._loadToken;
      this._userScrolled = false;
      let doc = null;
      try {
        this.loading = true;
        this.error = "";
        // No Authorization/custom header: the PDF is a public static file, so a plain
        // cross-origin GET (Range is CORS-safelisted) avoids a preflight.
        this.plog("PDF_DOWNLOAD_START"); // TEMP-PERF
        this._docId = ++_pdfDocSeq; // TEMP-PDF-DEBUG
        this.pdbg(`getDocument START url=${url}`); // TEMP-PDF-DEBUG
        const task = markRaw(pdfjsLib.getDocument({
          url,
          withCredentials: false,
          disableAutoFetch: true,
          disableStream: false,
        }));
        this._loadingTask = task;
        doc = await task.promise;
        this.pdbg(`getDocument END numPages=${doc.numPages}`); // TEMP-PDF-DEBUG
        this.plog("PDF_DOWNLOAD_END/PDF_DOCUMENT_OPENED"); // TEMP-PERF
        // The prop may have changed again while we awaited — discard the stale result.
        if (this.pdfUrl !== url) {
          doc.destroy();
          doc = null;
          return;
        }
        this.pdf = markRaw(doc);
        this._sampledSizes = new Set();
        this.totalPages = this.pdf.numPages;
        this.currentPage = 1;
        this.pageInput = 1;
        // FIRST-PAINT PATH: only page 1's real geometry is needed to lay out and
        // render the first page. Progress, annotations and the remaining page sizes
        // are deferred to finishLoadInBackground() so "Loading PDF..." disappears as
        // soon as page 1 can show — it must NOT wait on progress or annotations.
        this.plog("PDF_PARSE_START"); // TEMP-PERF
        await this.preparePageSizes(1);
        this.plog("PDF_PARSE_END"); // TEMP-PERF
        this.$nextTick(() => {
          if (token !== this._loadToken || this.pdf !== doc) return;
          this.setupObservers();   // attaching observers renders the visible page 1
          this.flushOfflineQueue();
        });
      } catch (error) {
        if (this.pdfUrl === url) this.error = "Unable to open this PDF.";
      } finally {
        this._loadingTask = null;
        if (this._loadingUrl === url) this._loadingUrl = null;
        this.loading = false;
        this.plog("PDF_SPINNER_HIDDEN"); // TEMP-PERF (this is when "Loading PDF..." is removed)
      }

      // BACKGROUND (fire-and-forget): remaining page sizes, progress and annotations.
      // Deliberately NOT awaited, so none of it can delay first paint. Guarded by the
      // captured token + document identity so a newer load or an unmount makes it a no-op.
      if (doc && this.pdf === doc && token === this._loadToken) {
        this.finishLoadInBackground(token, doc);
      }
    },
    // Runs AFTER the spinner is gone. Refines the remaining page geometry, then loads
    // progress + annotations, then restores saved scroll — re-checking the load token
    // and document identity at every step so nothing mutates state after an unmount or
    // a newer load. Each network call already swallows its own errors.
    async finishLoadInBackground(token, doc) {
      try {
        await this.preparePageSizes(this.totalPages, token);
      } catch (e) { /* size refinement is best-effort */ }
      if (token !== this._loadToken || this.pdf !== doc) return;

      this.plog("PDF_PROGRESS_START"); // TEMP-PERF
      await Promise.all([
        Promise.resolve(this.loadAnnotations()).catch(() => {}),
        Promise.resolve(this.loadProgress()).catch(() => {}),
      ]);
      this.plog("PDF_PROGRESS_END"); // TEMP-PERF
      if (token !== this._loadToken || this.pdf !== doc) return;

      // Restore saved scroll only if the user has NOT already scrolled/interacted, so a
      // late progress response never yanks them off the page they are reading. Loading
      // annotations only updates the reactive overlay — it never re-renders canvases.
      this.$nextTick(() => {
        if (token !== this._loadToken || this.pdf !== doc) return;
        this.restoreScrollPosition();
      });
    },
    async cancelActiveLoad() {
      try {
        if (this._loadingTask) await this._loadingTask.destroy();
      } catch (e) { /* ignore cancellation errors */ }
      this._loadingTask = null;
      try {
        if (this.pdf) await this.pdf.destroy();
      } catch (e) { /* ignore */ }
      this.pdf = null;
    },
    // Feature-detect the optional backend: on "not implemented" style responses,
    // switch off remote sync for the session so we stop retrying dead endpoints.
    // Returns true if remote sync was disabled (unavailable), false for transient errors.
    handleSyncError(error) {
      const status = error && error.response && error.response.status;
      if (status === 404 || status === 405 || status === 501) {
        this.remoteSyncDisabled = true;
        this.saveState = "local";
        return true;
      }
      return false;
    },
    resetViewer() {
      this.destroyAllCanvases();
      // Destroy the previous document / in-flight loading task (also nulls this.pdf).
      this.cancelActiveLoad();
      this.totalPages = 0;
      this.currentPage = 1;
      this.pageInput = 1;
      this.renderWindowStart = 1;
      this.renderWindowEnd = 1;
      this.pageSizes = {};
      this._sampledSizes = new Set();
      this.pageHeights = {};
      this.annotationsByPage = {};
      this.searchHighlights = {};
      this.searchResults = [];
      this.renderedPages = new Set();
      this.renderedThumbs = new Set();
      this.pendingRenderPages = new Set();
      this.renderEpoch += 1;
    },
    // Measure real page geometry. `limit` bounds how many leading pages are sampled
    // this call: first paint passes 1 (only page 1 is needed to lay out and render the
    // first page); a later background pass passes totalPages to refine the rest.
    // Already-sampled pages are skipped, so calling twice is cheap and idempotent.
    // `token` (background pass only) lets a stale/unmounted call bail mid-loop. The
    // render window is initialised on the FIRST-PAINT call only, so a background refine
    // never shrinks a window the user's scrolling has already expanded.
    async preparePageSizes(limit, token) {
      const sampleLimit = Math.min(this.totalPages, Math.max(1, limit || this.totalPages));
      this._sampledSizes = this._sampledSizes || new Set();
      // TEMP-PERF (remove after Android measurement): time the whole pass + each page.
      this.plog(`preparePageSizes:start pass=${token == null ? 'initial' : 'background'} count=${sampleLimit} alreadySampled=${this._sampledSizes.size}`);
      const _prepT0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      let _measuredThisPass = 0;
      let averageRatio = this.pageSizes[1] ? (this.pageSizes[1].height / this.pageSizes[1].width) : 1.35;
      for (let pageNumber = 1; pageNumber <= sampleLimit; pageNumber++) {
        if (token != null && token !== this._loadToken) return; // stale load / unmounted
        if (this._sampledSizes.has(pageNumber)) {
          averageRatio = this.pageSizes[pageNumber].height / this.pageSizes[pageNumber].width;
          continue;
        }
        if (!this.pdf) return;
        const _sizeT0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        this.pdbg(`getPage page=${pageNumber} caller=preparePageSizes pass=${token == null ? "initial" : "background"}`); // TEMP-PDF-DEBUG
        const page = await this.pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1, rotation: this.rotation });
        this.pageSizes[pageNumber] = { width: viewport.width, height: viewport.height };
        this._sampledSizes.add(pageNumber);
        averageRatio = viewport.height / viewport.width;
        _measuredThisPass += 1;
        // TEMP-PERF
        const _sizeMs = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - _sizeT0;
        this.plog(`page-size page=${pageNumber} w=${Math.round(viewport.width)} h=${Math.round(viewport.height)} duration=${_sizeMs.toFixed(1)}ms`);
      }
      // Not-yet-measured pages get a ratio-correct placeholder derived from page 1 —
      // never an A4 assumption; the background pass replaces these with real sizes.
      for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
        if (!this.pageSizes[pageNumber]) this.pageSizes[pageNumber] = { width: 612, height: 612 * averageRatio };
      }
      this.recalculateHeights();
      if (token == null) this.renderWindowEnd = Math.min(this.totalPages, 12);
      // TEMP-PERF
      const _prepMs = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - _prepT0;
      this.plog(`preparePageSizes:end measuredThisPass=${_measuredThisPass} totalSampled=${this._sampledSizes.size} duration=${_prepMs.toFixed(1)}ms`);
    },
    sumPageHeights(start, end) {
      if (!this.totalPages || end < start) return 0;
      let total = 0;
      for (let page = Math.max(1, start); page <= Math.min(this.totalPages, end); page++) {
        total += this.pageHeights[page] || 820;
      }
      return total;
    },
    pageFromScroll(scrollTop) {
      let total = 0;
      for (let page = 1; page <= this.totalPages; page++) {
        total += this.pageHeights[page] || 820;
        if (total >= scrollTop) return page;
      }
      return this.totalPages || 1;
    },
    updateRenderWindowAround(page) {
      const start = Math.max(1, page - this.renderBuffer);
      const end = Math.min(this.totalPages, page + this.renderBuffer + 6);
      if (start === this.renderWindowStart && end === this.renderWindowEnd) return;
      this.renderWindowStart = start;
      this.renderWindowEnd = end;
      this.$nextTick(() => {
        this.setupObservers();
        // Priority: render the CURRENT page first, then its immediate neighbours.
        // The rest of the mounted window is rendered lazily by the
        // IntersectionObserver as pages approach the viewport (far pages wait, and
        // pages the user flings past are cancelled in cleanupFarPages) — so we no
        // longer eagerly render the whole ~11-page window on every window change.
        this.renderPage(page);
        this.renderPage(page + 1);
        this.renderPage(page - 1);
        this.cleanupFarPages();
      });
    },
    // Available content width for a page = scroll-area inner width minus the
    // .page-shell horizontal padding (12px each side). Cached in contentWidth so
    // page geometry never triggers a DOM read on scroll. Only ONE layout read here.
    measureContentWidth() {
      const area = this.$refs.scrollArea;
      if (!area) return;
      const SHELL_PADDING_X = 24; // .page-shell padding: 12px * 2
      this.contentWidth = Math.max((area.clientWidth || 0) - SHELL_PADDING_X, 200);
    },
    recalculateHeights() {
      // Refresh the cached available width (load/zoom/resize call this — never scroll).
      this.measureContentWidth();
      // Fit width = available viewer width * user zoom (zoom=1 => fit-to-width).
      const fitWidth = (this.contentWidth || 360) * this.zoom;
      this.pageNumbers.forEach((page) => {
        const size = this.pageSizes[page] || { width: 612, height: 792 };
        // Height is derived from THIS page's real aspect ratio — no A4 assumption,
        // no stretch/squash. +24 reserves the .page-shell vertical padding.
        const scale = fitWidth / size.width;
        this.pageHeights[page] = Math.ceil(size.height * scale + 24);
      });
    },
    pageFrameStyle(page) {
      const size = this.pageSizes[page] || { width: 612, height: 792 };
      // Fit the page WIDTH to the available viewer width (× zoom); derive the
      // HEIGHT from the page's actual width/height ratio. Cheap arithmetic only —
      // no DOM read — so this is safe to evaluate during render.
      const width = (this.contentWidth || 360) * this.zoom;
      const height = width * (size.height / size.width);
      return { width: `${width}px`, height: `${height}px` };
    },
    setupObservers() {
      if (this.observer) this.observer.disconnect();
      if (this.thumbObserver) this.thumbObserver.disconnect();
      this.observer = new IntersectionObserver(this.onPagesIntersect, {
        root: this.$refs.scrollArea,
        rootMargin: "900px 0px",
        threshold: 0.01,
      });
      this.thumbObserver = new IntersectionObserver(this.onThumbsIntersect, {
        root: this.$el.querySelector(".thumbs-panel"),
        rootMargin: "500px 0px",
        threshold: 0.01,
      });
      this.$el.querySelectorAll(".page-shell").forEach((el) => this.observer.observe(el));
      this.$el.querySelectorAll(".thumb").forEach((el) => this.thumbObserver.observe(el));
    },
    onPagesIntersect(entries) {
      entries.forEach((entry) => {
        const page = Number(entry.target.dataset.page);
        this.rlog({ page, event: "intersection", intersecting: entry.isIntersecting }); // TEMP-RENDER-DEBUG
        if (entry.isIntersecting) {
          this.visiblePages.add(page);
          this.renderPage(page);
        } else {
          this.visiblePages.delete(page);
        }
      });
      this.updateCurrentPage();
      this.cleanupFarPages();
    },
    onThumbsIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.renderThumbnail(Number(entry.target.dataset.thumbPage));
      });
    },
    setCanvasRef(page, el) {
      if (el) {
        const isNew = this.pageCanvases[page] !== el;
        this.pageCanvases[page] = el;
        if (isNew) this.rlog({ page, event: "canvas-created" }); // TEMP-RENDER-DEBUG
        return;
      }
      // el === null. IMPORTANT: because the template uses an inline function ref, Vue
      // fires this null on EVERY re-render (old-ref cleanup), not only on real unmount
      // — the new ref re-sets the element immediately after. So distinguish the two:
      // a page still inside the mounted window [renderWindowStart..renderWindowEnd] is
      // a transient re-render null → ignore it (original behaviour). A page now OUTSIDE
      // the window has genuinely been virtualized away by a scroll.
      //
      // The original code ignored null unconditionally, which on a true unmount left
      // THREE stale traces: the detached canvas ref in pageCanvases, the page still in
      // renderedPages, and — because unmounting an observed element fires NO
      // IntersectionObserver "not-intersecting" callback — a phantom entry in
      // visiblePages. When the page returned with a fresh BLANK canvas, renderPage()'s
      // `renderedPages.has(page)` guard short-circuited and it stayed blank until
      // reload. Forget the page on a real unmount so its return triggers a re-render.
      // THIS is the fast-scroll blank-page fix.
      if (page >= this.renderWindowStart && page <= this.renderWindowEnd) return;
      if (!this.pageCanvases[page]) return;
      this._renderTasks = this._renderTasks || {};
      const task = this._renderTasks[page];
      if (task) { try { task.cancel(); } catch (e) { /* already settled */ } delete this._renderTasks[page]; }
      this.renderingPages[page] = false;
      this.pendingRenderPages.delete(page);
      this.renderedPages.delete(page);
      this.visiblePages.delete(page);
      delete this.pageCanvases[page];
      this.rlog({ page, event: "canvas-removed" }); // TEMP-RENDER-DEBUG
    },
    setThumbRef(page, el) {
      if (el) this.thumbCanvases[page] = el;
    },
    async renderPage(pageNumber, force = false) {
      if (!this.pdf || pageNumber < 1 || pageNumber > this.totalPages) return;
      // Already rendered at the current size/epoch — skip. Prevents the observer
      // and window updates from re-rendering the same page repeatedly during fast
      // scrolling (a major source of the stutter).
      if (!force && this.renderedPages.has(pageNumber)) {
        this.rlog({ page: pageNumber, event: "render-skipped", reason: "already-rendered" }); // TEMP-RENDER-DEBUG
        this.pdbg(`render SKIP page=${pageNumber} reason=already-rendered`); // TEMP-PDF-DEBUG
        return;
      }
      if (this.renderingPages[pageNumber]) {
        this.pendingRenderPages.add(pageNumber);
        this.rlog({ page: pageNumber, event: "render-pending" }); // TEMP-RENDER-DEBUG
        return;
      }
      const canvas = this.pageCanvases[pageNumber];
      // No live, attached canvas → nothing to paint onto. Bail instead of rendering
      // to a detached node (which never becomes visible and used to throw on
      // frame.clientWidth, surfacing a spurious "A page failed to render.").
      if (!canvas || !canvas.parentElement) {
        this.rlog({ page: pageNumber, event: "render-skipped", reason: canvas ? "detached-canvas" : "no-canvas" }); // TEMP-RENDER-DEBUG
        return;
      }
      this.renderingPages[pageNumber] = true;
      const renderEpoch = this.renderEpoch;
      // TEMP-RENDER-DEBUG: `inflight` = number of pages rendering concurrently — the
      // key metric for the fast-scroll stutter (uncapped concurrent 4K rasterization).
      const _inflight = Object.values(this.renderingPages).filter(Boolean).length;
      this.rlog({ page: pageNumber, event: "render-start", force, inflight: _inflight }); // TEMP-RENDER-DEBUG
      // TEMP-PDF-DEBUG: per-page render counter — count>1 == this page rasterized AGAIN
      // (destroyed by cleanup then re-rendered). Note this is CPU re-work, not a re-fetch:
      // pdf.js caches the page's byte ranges, so an AGAIN never re-hits the network.
      this._renderCounts = this._renderCounts || {};
      this._renderCounts[pageNumber] = (this._renderCounts[pageNumber] || 0) + 1;
      const _rcount = this._renderCounts[pageNumber];
      this.pdbg(`render START page=${pageNumber} count=${_rcount}${_rcount > 1 ? " (AGAIN)" : ""} force=${force}`); // TEMP-PDF-DEBUG
      // TEMP-PERF: measure the rasterization cost of this (4K) page.
      const _renderT0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      if (pageNumber === 1) this.plog("PDF_PAGE_1_START"); // TEMP-PERF
      try {
        this.pdbg(`getPage page=${pageNumber} caller=renderPage`); // TEMP-PDF-DEBUG
        const page = await this.pdf.getPage(pageNumber);
        // The canvas can be unmounted while getPage() is awaited (fast scroll moved
        // the window). If so, its frame is gone — skip rather than throw; the page
        // will re-render from a fresh canvas when it re-enters the window.
        const frame = canvas.parentElement;
        if (!frame || this.pageCanvases[pageNumber] !== canvas) {
          this.rlog({ page: pageNumber, event: "render-skipped", reason: "detached-mid-render" }); // TEMP-RENDER-DEBUG
          return;
        }
        const viewportBase = page.getViewport({ scale: 1, rotation: this.rotation });
        this.pageSizes[pageNumber] = { width: viewportBase.width, height: viewportBase.height };
        const scale = frame.clientWidth / viewportBase.width;
        const viewport = page.getViewport({ scale, rotation: this.rotation });
        // Cap the backing-store DPR at 2: 2x is retina-sharp on essentially every
        // phone, while avoiding the ~2.25x-larger canvas allocations that a 3x
        // device would otherwise create for every page.
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        const context = canvas.getContext("2d", { alpha: false });
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        // Keep the pdf.js RenderTask so an obsolete render (page the user flung
        // past) can be cancelled in destroyPageCanvas instead of finishing off-screen.
        const task = page.render({ canvasContext: context, viewport });
        this._renderTasks[pageNumber] = task;
        await task.promise;
        this.renderedPages.add(pageNumber);
        // TEMP-PERF: render-end now carries the rasterization duration so the device
        // console shows exactly how long each 4K page takes and how they overlap.
        const _renderMs = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - _renderT0;
        this.rlog({ page: pageNumber, event: "render-end", durationMs: _renderMs.toFixed(0) }); // TEMP-RENDER-DEBUG
        this.pdbg(`render END page=${pageNumber} durationMs=${_renderMs.toFixed(0)}`); // TEMP-PDF-DEBUG
        if (pageNumber === 1) this.plog("PDF_PAGE_1_END"); // TEMP-PERF
        if (!this._firstPaintLogged) { this._firstPaintLogged = true; this.plog("PDF_FIRST_VISIBLE"); } // TEMP-PERF
        if (this.renderedPages.size === this.totalPages) this.plog("PDF_ALL_PAGES_END"); // TEMP-PERF
      } catch (e) {
        // A cancelled render (obsolete page) is expected — not a real error.
        if (!(e && e.name === 'RenderingCancelledException')) {
          this.error = "A page failed to render.";
        }
      } finally {
        delete this._renderTasks[pageNumber];
        this.renderingPages[pageNumber] = false;
        if (this.pendingRenderPages.has(pageNumber) || renderEpoch !== this.renderEpoch) {
          this.pendingRenderPages.delete(pageNumber);
          this.$nextTick(() => this.renderPage(pageNumber, true));
        }
      }
    },
    async renderThumbnail(pageNumber) {
      if (!this.pdf || this.renderedThumbs.has(pageNumber)) return;
      const canvas = this.thumbCanvases[pageNumber];
      if (!canvas) return;
      this.pdbg(`getPage page=${pageNumber} caller=renderThumbnail`); // TEMP-PDF-DEBUG
      const page = await this.pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: 0.18, rotation: this.rotation });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
      this.renderedThumbs.add(pageNumber);
    },
    cleanupFarPages() {
      const keep = new Set();
      this.visiblePages.forEach((page) => {
        for (let i = page - 2; i <= page + 2; i++) if (i >= 1 && i <= this.totalPages) keep.add(i);
      });
      this.renderedPages.forEach((page) => {
        if (!keep.has(page)) {
          this.rlog({ page, event: "cleanup" }); // TEMP-RENDER-DEBUG
          this.destroyPageCanvas(page);
        }
      });
    },
    destroyPageCanvas(page) {
      // Cancel any in-flight render for a page being evicted (e.g. flung past) so
      // obsolete work stops immediately instead of running to completion off-screen.
      const task = this._renderTasks[page];
      if (task) {
        try { task.cancel(); } catch (e) { /* already settled */ }
        delete this._renderTasks[page];
      }
      this.renderingPages[page] = false;
      const canvas = this.pageCanvases[page];
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.width = 0;
        canvas.height = 0;
      }
      if (this.renderedPages.has(page)) this.pdbg(`destroy page=${page} (canvas evicted, page bytes stay cached)`); // TEMP-PDF-DEBUG
      this.renderedPages.delete(page);
    },
    destroyAllCanvases() {
      Object.keys(this.pageCanvases).forEach((page) => this.destroyPageCanvas(Number(page)));
      Object.values(this.thumbCanvases).forEach((canvas) => {
        if (canvas) {
          canvas.width = 0;
          canvas.height = 0;
        }
      });
    },
    rerenderVisiblePages() {
      const pages = Array.from(this.visiblePages);
      pages.forEach((page) => this.destroyPageCanvas(page));
      this.$nextTick(() => pages.forEach((page) => this.renderPage(page)));
    },
    goToPage(page) {
      const target = Math.min(Math.max(Number(page) || 1, 1), this.totalPages || 1);
      this.updateRenderWindowAround(target);
      this.$nextTick(() => {
        if (this.$refs.scrollArea) {
          this.$refs.scrollArea.scrollTo({ top: this.sumPageHeights(1, target - 1), behavior: "smooth" });
        }
      });
      this.currentPage = target;
      this.pageInput = target;
      this.markProgressDirty();
    },
    onScroll() {
      // Keep the scroll handler CHEAP: reset the 3s progress debounce (a timer,
      // not a network call), then coalesce the viewport/render bookkeeping into
      // at most ONE run per animation frame. No PDF rendering happens here — the
      // rAF callback only recomputes the viewport and schedules render work.
      // Once the user has scrolled, a late background progress-restore must not yank
      // them back to a saved position (see restoreScrollPosition).
      this._userScrolled = true;
      this.markProgressDirty();
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this.updateRenderWindowAround(this.pageFromScroll(this.$refs.scrollArea?.scrollTop || 0));
        this.updateCurrentPage();
      });
    },
    onWheel(event) {
      if (!event.ctrlKey) return;
      event.preventDefault();
      this.setZoom(this.zoom + (event.deltaY < 0 ? 0.12 : -0.12));
    },
    onTouchStart(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      this.touchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    },
    onTouchEnd(event) {
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch || !this.touchStart) return;
      const dx = touch.clientX - this.touchStart.x;
      const dy = touch.clientY - this.touchStart.y;
      const quick = Date.now() - this.touchStart.time < 500;
      if (quick && Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 1.8) {
        this.goToPage(this.currentPage + (dx < 0 ? 1 : -1));
      }
      this.touchStart = null;
    },
    updateCurrentPage() {
      const area = this.$refs.scrollArea;
      if (!area) return;
      const page = this.pageFromScroll(area.scrollTop + 24);
      this.currentPage = page;
      this.pageInput = page;
    },
    setZoom(value) {
      this.applyZoom(value);
    },
    fitWidth() {
      // New model: pages are ALWAYS fitted to the available width at zoom=1.
      this.applyZoom(1);
    },
    fitPage() {
      // Fit the whole current page in view: zoom=1 is already width-fit; shrink
      // further only if the page is taller than the viewport (never enlarge past 1).
      const area = this.$refs.scrollArea;
      const size = this.pageSizes[this.currentPage] || { width: 612, height: 792 };
      this.measureContentWidth();
      const width = this.contentWidth || 360;
      const pageHeightAtFit = width * (size.height / size.width);
      const availableHeight = Math.max((area?.clientHeight || window.innerHeight) - 40, 240);
      this.applyZoom(Math.min(1, availableHeight / pageHeightAtFit));
    },
    applyZoom(value) {
      const area = this.$refs.scrollArea;
      const nextZoom = Math.min(Math.max(Number(value) || 1, 0.25), 3);
      if (Math.abs(nextZoom - this.zoom) < 0.001) return;

      const anchorPage = this.currentPage || this.pageFromScroll(area?.scrollTop || 0);
      const previousPageTop = this.sumPageHeights(1, anchorPage - 1);
      const previousPageHeight = this.pageHeights[anchorPage] || 820;
      const offsetRatio = area
        ? Math.min(Math.max((area.scrollTop - previousPageTop) / previousPageHeight, 0), 1)
        : 0;

      this.zoom = nextZoom;
      this.$nextTick(() => {
        const nextPageTop = this.sumPageHeights(1, anchorPage - 1);
        const nextPageHeight = this.pageHeights[anchorPage] || 820;
        if (this.$refs.scrollArea) {
          this.$refs.scrollArea.scrollTop = Math.max(0, nextPageTop + nextPageHeight * offsetRatio);
        }
        this.updateRenderWindowAround(anchorPage);
        this.updateCurrentPage();
      });
    },
    rotatePage() {
      this.rotation = (this.rotation + 90) % 360;
      this.renderedThumbs = new Set();
    },
    onKeydown(event) {
      if (event.ctrlKey && ["s", "p"].includes(String(event.key).toLowerCase())) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.target && ["INPUT", "TEXTAREA"].includes(event.target.tagName)) return;
      if (event.ctrlKey && (event.key === "+" || event.key === "=")) {
        event.preventDefault();
        this.setZoom(this.zoom + 0.15);
      }
      if (event.ctrlKey && event.key === "-") {
        event.preventDefault();
        this.setZoom(this.zoom - 0.15);
      }
      if (event.key === "PageUp") {
        event.preventDefault();
        this.goToPage(this.currentPage - 1);
      }
      if (event.key === "PageDown") {
        event.preventDefault();
        this.goToPage(this.currentPage + 1);
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") this.goToPage(this.currentPage - 1);
      if (event.key === "ArrowRight" || event.key === "ArrowDown") this.goToPage(this.currentPage + 1);
      if (event.key === "Escape" && this.isFullscreen) this.exitFullscreen();
    },
    async toggleFullscreen() {
      if (this.isFullscreen) return this.exitFullscreen();
      if (this.$el.requestFullscreen) await this.$el.requestFullscreen();
      this.isFullscreen = true;
      document.body.classList.add("pdf-reader-fullscreen");
    },
    async exitFullscreen() {
      if (document.fullscreenElement && document.exitFullscreen) await document.exitFullscreen();
      this.exitFullscreenClass();
    },
    exitFullscreenClass() {
      this.isFullscreen = false;
      document.body.classList.remove("pdf-reader-fullscreen");
    },
    onFullscreenChange() {
      if (!document.fullscreenElement) this.exitFullscreenClass();
    },
    getPoint(event) {
      const svg = event.currentTarget;
      const rect = svg.getBoundingClientRect();
      return {
        x: Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1),
        y: Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1),
      };
    },
    onAnnotationPointerDown(event, page) {
      if (this.readOnly || this.activeTool === "pan") return;
      event.preventDefault();
      const point = this.getPoint(event);
      if (this.activeTool === "text") {
        const text = window.prompt("Text");
        if (text) this.addAnnotation(page, { type: "text", x: point.x, y: point.y, text, color: this.strokeColor, size: 0.035 });
        return;
      }
      if (this.activeTool === "eraser") {
        this.eraseAt(page, point);
        return;
      }
      const base = { id: this.makeId(), color: this.strokeColor };
      if (this.activeTool === "pen") this.drawing = { page, item: { ...base, type: "pen", points: [point], width: 3, opacity: 1 } };
      if (this.activeTool === "highlighter") this.drawing = { page, item: { ...base, type: "highlighter", points: [point], width: 12, opacity: 0.35, color: "#f5d90a" } };
      if (this.activeTool === "rectangle" || this.activeTool === "circle" || this.activeTool === "arrow") {
        this.drawing = { page, item: { ...base, type: this.activeTool, start: point, end: point } };
      }
      if (this.drawing) {
        this.pushUndo(page);
        this.annotationsByPage[page] = [...(this.annotationsByPage[page] || []), this.drawing.item];
      }
    },
    onAnnotationPointerMove(event) {
      if (!this.drawing) return;
      const point = this.getPoint(event);
      const item = this.drawing.item;
      if (item.points) item.points.push(point);
      else item.end = point;
      this.markDirty(this.drawing.page);
    },
    onAnnotationPointerUp() {
      if (!this.drawing) return;
      this.markDirty(this.drawing.page);
      this.drawing = null;
    },
    addAnnotation(page, item) {
      this.pushUndo(page);
      this.annotationsByPage[page] = [...(this.annotationsByPage[page] || []), { id: this.makeId(), ...item }];
      this.markDirty(page);
    },
    eraseAt(page, point) {
      const list = this.annotationsByPage[page] || [];
      const index = list.findIndex((item) => this.annotationNearPoint(item, point));
      if (index >= 0) {
        this.pushUndo(page);
        this.annotationsByPage[page] = list.filter((_, i) => i !== index);
        this.markDirty(page);
      }
    },
    annotationNearPoint(item, point) {
      const threshold = 0.025;
      if (item.points) return item.points.some((p) => Math.abs(p.x - point.x) < threshold && Math.abs(p.y - point.y) < threshold);
      if (item.start && item.end) {
        const box = this.rectFromItem(item);
        return point.x >= box.x - threshold && point.x <= box.x + box.width + threshold && point.y >= box.y - threshold && point.y <= box.y + box.height + threshold;
      }
      return Math.abs(item.x - point.x) < threshold && Math.abs(item.y - point.y) < threshold;
    },
    pushUndo(page) {
      this.undoStack[page] = this.undoStack[page] || [];
      this.redoStack[page] = [];
      this.undoStack[page].push(JSON.stringify(this.annotationsByPage[page] || []));
    },
    undo() {
      const page = this.currentPage;
      const stack = this.undoStack[page] || [];
      if (!stack.length) return;
      this.redoStack[page] = this.redoStack[page] || [];
      this.redoStack[page].push(JSON.stringify(this.annotationsByPage[page] || []));
      this.annotationsByPage[page] = JSON.parse(stack.pop());
      this.markDirty(page);
    },
    redo() {
      const page = this.currentPage;
      const stack = this.redoStack[page] || [];
      if (!stack.length) return;
      this.pushUndo(page);
      this.annotationsByPage[page] = JSON.parse(stack.pop());
      this.markDirty(page);
    },
    clearPage() {
      this.pushUndo(this.currentPage);
      this.annotationsByPage[this.currentPage] = [];
      this.markDirty(this.currentPage);
    },
    markDirty(page) {
      this.dirtyPages.add(page);
      this.saveState = navigator.onLine ? "saving" : "offline";
      this.saveLocalBackup();
    },
    async loadAnnotations() {
      if (this.remoteSyncDisabled) {
        const local = this.readLocalBackup();
        this.annotationsByPage = (local && local.annotationsByPage) || {};
        return;
      }
      try {
        const response = await axios.get(`/pdf-reader/${this.contentId}/annotations`);
        this.annotationsByPage = response.data.data || {};
        const local = this.readLocalBackup();
        if (local) this.annotationsByPage = { ...this.annotationsByPage, ...local.annotationsByPage };
      } catch (e) {
        this.handleSyncError(e);
        const local = this.readLocalBackup();
        if (local) this.annotationsByPage = local.annotationsByPage || {};
      }
    },
    async flushAnnotations() {
      if (this.readOnly || !this.dirtyPages.size) return;
      // Always persist locally first, so nothing is lost regardless of the backend.
      this.saveLocalBackup();
      if (this.remoteSyncDisabled) {
        this.dirtyPages.clear();
        this.saveState = "local";
        return;
      }
      if (this._savingAnnotations) return; // single-flight: no overlapping POSTs
      this._savingAnnotations = true;
      const pages = Array.from(this.dirtyPages).map((page) => ({
        page_number: Number(page),
        annotation_json: this.annotationsByPage[page] || [],
      }));
      try {
        await axios.post(`/pdf-reader/${this.contentId}/annotations`, { pages });
        this.dirtyPages.clear();
        localStorage.removeItem(this.offlineKey);
        this.saveState = "saved";
      } catch (e) {
        if (this.handleSyncError(e)) {
          this.dirtyPages.clear(); // backend gone: on-device copy already saved above
        } else {
          this.queueOffline(pages);
          this.saveState = navigator.onLine ? "error" : "offline";
        }
      } finally {
        this._savingAnnotations = false;
      }
    },
    queueOffline(pages) {
      const queued = JSON.parse(localStorage.getItem(`${this.offlineKey}-queue`) || "[]");
      localStorage.setItem(`${this.offlineKey}-queue`, JSON.stringify([...queued, ...pages]));
      this.saveLocalBackup();
    },
    async flushOfflineQueue() {
      if (this.remoteSyncDisabled) return;
      const key = `${this.offlineKey}-queue`;
      const queued = JSON.parse(localStorage.getItem(key) || "[]");
      if (!queued.length) return;
      const byPage = {};
      queued.forEach((page) => { byPage[page.page_number] = page; });
      try {
        await axios.post(`/pdf-reader/${this.contentId}/annotations`, { pages: Object.values(byPage) });
        localStorage.removeItem(key);
        this.saveState = "saved";
      } catch (e) {
        if (!this.handleSyncError(e)) this.saveState = "error";
      }
    },
    saveLocalBackup() {
      localStorage.setItem(this.offlineKey, JSON.stringify({
        annotationsByPage: this.annotationsByPage,
        updatedAt: Date.now(),
      }));
    },
    readLocalBackup() {
      try {
        return JSON.parse(localStorage.getItem(this.offlineKey));
      } catch (e) {
        return null;
      }
    },
    async loadProgress() {
      if (this.remoteSyncDisabled) { this.scrollRestore = null; return; }
      try {
        this.loadingProgress = true;
        const response = await axios.get(`/pdf-reader/${this.contentId}/progress`);
        const progress = response.data.data || {};
        this.zoom = Number(progress.zoom || 1);
        this.lastSavedProgress = this.normalizeProgress({
          last_page: Number(progress.last_page || 1),
          zoom: Number(progress.zoom || 1),
          scroll_position: Number(progress.scroll_position || 0),
        });
        this.scrollRestore = {
          lastPage: Number(progress.last_page || 1),
          scrollPosition: Number(progress.scroll_position || 0),
        };
      } catch (e) {
        this.handleSyncError(e);
        this.scrollRestore = null;
      } finally {
        this.loadingProgress = false;
      }
    },
    restoreScrollPosition() {
      // Skip if there is nothing to restore, or if the user already scrolled/interacted
      // since this load — progress now arrives in the background, so restoring after the
      // user has started reading would unexpectedly jump their page.
      if (!this.scrollRestore || this._userScrolled) return;
      this.loadingProgress = true;
      if (this.scrollRestore.scrollPosition > 0) {
        this.updateRenderWindowAround(this.pageFromScroll(this.scrollRestore.scrollPosition));
        this.$refs.scrollArea.scrollTop = this.scrollRestore.scrollPosition;
      } else {
        this.goToPage(this.scrollRestore.lastPage);
      }
      setTimeout(() => {
        this.loadingProgress = false;
        this.progressDirty = false;
      }, 700);
    },
    // 3-second debounce for progress saving. Every progress/page change resets
    // the SINGLE pending timer (clearTimeout first, so there can never be more
    // than one), and the POST only fires after 3s with no further change —
    // sending only the latest progress value. Continuous scrolling therefore
    // produces NO requests until it stops for 3s.
    queueProgressSave() {
      clearTimeout(this._progressTimeout);
      this._progressTimeout = setTimeout(this.saveProgress, 3000);
    },
    markProgressDirty() {
      if (!this.pdf || this.loadingProgress) return;
      this.progressDirty = true;
      this.queueProgressSave();
    },
    normalizeProgress(progress) {
      return {
        last_page: Number(progress.last_page || 1),
        zoom: Number(Number(progress.zoom || 1).toFixed(3)),
        scroll_position: Number(progress.scroll_position || 0),
        total_pages: Number(progress.total_pages || 0),
      };
    },
    currentProgressPayload() {
      return this.normalizeProgress({
        last_page: this.currentPage,
        zoom: this.zoom,
        scroll_position: Math.max(0, Math.round(this.$refs.scrollArea?.scrollTop || 0)),
        total_pages: this.totalPages,
      });
    },
    async saveProgress(force = false) {
      if (!this.pdf || !this.$refs.scrollArea) return;
      const payload = this.currentProgressPayload();
      if (!force && !this.progressDirty) return;
      if (this.lastSavedProgress && JSON.stringify(payload) === JSON.stringify(this.lastSavedProgress)) {
        this.progressDirty = false;
        return;
      }
      if (this.remoteSyncDisabled) {
        localStorage.setItem(`pdf-reader-progress-${this.contentId}`, JSON.stringify(payload));
        this.lastSavedProgress = payload;
        this.progressDirty = false;
        return;
      }
      if (this._savingProgress) return; // single-flight: no overlapping POSTs
      this._savingProgress = true;
      try {
        // Sends only the LATEST progress, and only after the 3s debounce settles
        // (see queueProgressSave). Not called on every scroll — the scroll handler
        // just resets the single debounce timer.
        await axios.post(`/pdf-reader/${this.contentId}/progress`, payload);
        this.lastSavedProgress = payload;
        this.progressDirty = false;
      } catch (e) {
        this.handleSyncError(e);
        localStorage.setItem(`pdf-reader-progress-${this.contentId}`, JSON.stringify(payload));
        // Clear the dirty flag so a persistent failure isn't retried every interval.
        this.progressDirty = false;
      } finally {
        this._savingProgress = false;
      }
    },
    async runSearch() {
      if (!this.searchQuery || !this.pdf) return;
      this.searchResults = [];
      this.searchHighlights = {};
      const query = this.searchQuery.toLowerCase();
      for (let pageNumber = 1; pageNumber <= this.totalPages; pageNumber++) {
        const page = await this.pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1, rotation: this.rotation });
        const text = await page.getTextContent();
        const combinedText = text.items.map((item) => String(item.str || "")).join(" ").toLowerCase();
        let pageMatched = false;
        text.items.forEach((item) => {
          const value = String(item.str || "");
          if (!value.toLowerCase().includes(query)) return;
          const tx = pdfjsLib.Util.transform(viewport.transform, item.transform);
          const x = tx[4] / viewport.width;
          const y = 1 - (tx[5] / viewport.height);
          const width = Math.min(Math.max((item.width || value.length * 6) / viewport.width, 0.03), 0.9);
          const height = Math.min(Math.max((item.height || 12) / viewport.height, 0.012), 0.06);
          this.searchHighlights[pageNumber] = [...(this.searchHighlights[pageNumber] || []), { x, y: y - height, width, height }];
          pageMatched = true;
        });
        if (!pageMatched && combinedText.includes(query)) {
          this.searchHighlights[pageNumber] = [{ x: 0.08, y: 0.08, width: 0.84, height: 0.055 }];
          pageMatched = true;
        }
        if (pageMatched) this.searchResults.push({ page: pageNumber });
        if (pageNumber % 20 === 0) await new Promise((resolve) => setTimeout(resolve, 0));
      }
      this.activeSearchIndex = this.searchResults.length ? 0 : -1;
      if (this.activeSearchIndex >= 0) this.goToPage(this.searchResults[0].page);
    },
    pointsToString(points) {
      return (points || []).map((point) => `${point.x},${point.y}`).join(" ");
    },
    rectFromItem(item) {
      const x = Math.min(item.start.x, item.end.x);
      const y = Math.min(item.start.y, item.end.y);
      return { x, y, width: Math.abs(item.end.x - item.start.x), height: Math.abs(item.end.y - item.start.y) };
    },
    ellipseFromItem(item) {
      const rect = this.rectFromItem(item);
      return { cx: rect.x + rect.width / 2, cy: rect.y + rect.height / 2, rx: rect.width / 2, ry: rect.height / 2 };
    },
    arrowHead(item) {
      const angle = Math.atan2(item.end.y - item.start.y, item.end.x - item.start.x);
      const size = 0.025;
      const a1 = angle + Math.PI * 0.82;
      const a2 = angle - Math.PI * 0.82;
      return `${item.end.x},${item.end.y} ${item.end.x + Math.cos(a1) * size},${item.end.y + Math.sin(a1) * size} ${item.end.x + Math.cos(a2) * size},${item.end.y + Math.sin(a2) * size}`;
    },
    makeId() {
      return `a-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    },
  },
};
</script>

<style scoped>
.pdf-learning-viewer {
  --toolbar: #202124;
  --panel: #2f3136;
  --paper: #fff;
  --line: #d8dde6;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: min(82vh, 920px);
  min-height: 620px;
  background: #3b3d42;
  border-radius: 12px;
  overflow: hidden;
  color: #f8fafc;
  direction: ltr;
}
.pdf-learning-viewer.fullscreen {
  position: fixed;
  inset: 0;
  z-index: 99999;
  height: 100vh;
  min-height: 100vh;
  border-radius: 0;
}
.pdf-toolbar {
  flex: 0 0 auto;
  background: var(--toolbar);
  border-bottom: 1px solid rgba(255,255,255,.08);
  min-width: 0;
}
.toolbar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 48px;
  padding: 6px 10px;
}
.secondary-row {
  border-top: 1px solid rgba(255,255,255,.06);
  flex-wrap: wrap;
}
.toolbar-spacer { flex: 1; }
button {
  font-family: inherit;
}
.icon-btn,
.text-btn,
.mini-btn,
.annotation-tools button,
.thumb-toggle,
.floating-thumbs {
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.08);
  color: #fff;
  border-radius: 7px;
  min-width: 36px;
  height: 36px;
  cursor: pointer;
}
.icon-btn:disabled {
  opacity: .35;
  cursor: not-allowed;
}
.text-btn {
  padding: 0 10px;
  width: auto;
}
.page-control {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dfe3ea;
}
.page-control input {
  width: 64px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,.18);
  background: #111827;
  color: #fff;
  text-align: center;
}
.zoom-label,
.save-state {
  min-width: 54px;
  font-size: 13px;
  color: #dfe3ea;
}
.save-state.offline,
.save-state.error {
  color: #fbbf24;
}
.search-box {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 8px;
  background: #111827;
  border-radius: 7px;
  border: 1px solid rgba(255,255,255,.12);
}
.search-box input {
  width: 170px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #fff;
}
.mini-btn {
  min-width: 34px;
  height: 26px;
}
.pdf-body {
  display: grid;
  grid-template-columns: 128px 1fr 48px;
  position: relative;
  min-height: 0;
  min-width: 0;
  flex: 1;
}
.thumbs-panel {
  background: #25272c;
  border-right: 1px solid rgba(255,255,255,.08);
  overflow-y: auto;
  padding: 10px 8px;
}
.thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 7px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  color: #cbd5e1;
}
.thumb.active {
  border-color: #60a5fa;
  background: rgba(96,165,250,.16);
}
.thumb canvas {
  width: 82px;
  min-height: 106px;
  background: #fff;
  border-radius: 3px;
}
.pdf-scroll {
  position: relative;
  overflow: auto;
  background: #4a4d54;
  min-width: 0;
  /* Keep the PDF's overscroll to itself: at the first/last page, a fast swipe
     must not chain to the parent page (which would let the browser/WebView
     trigger a pull-to-refresh or bounce). pan-y + pinch-zoom preserves normal
     vertical scrolling and pinch-zoom while suppressing the native pull gesture. */
  overscroll-behavior: contain;
  touch-action: pan-y pinch-zoom;
}
.page-shell {
  display: flex;
  justify-content: center;
  padding: 12px;
}
.virtual-spacer {
  flex: 0 0 auto;
}
.page-frame {
  position: relative;
  flex: 0 0 auto;
  background: var(--paper);
  box-shadow: 0 8px 28px rgba(0,0,0,.28);
}
.pdf-canvas,
.annotation-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.annotation-layer {
  touch-action: pan-y pinch-zoom;
}
.watermark-layer {
  color: rgba(20, 30, 45, .13);
  font-size: .026px;
  font-weight: 700;
  letter-spacing: 0;
  pointer-events: none;
  user-select: none;
}
.annotation-layer .search-highlight {
  fill: rgba(255, 230, 0, .42);
}
.annotation-highlighter {
  mix-blend-mode: multiply;
}
.page-loading,
.viewer-loading,
.viewer-error {
  position: absolute;
  inset: auto 16px 16px auto;
  background: rgba(17,24,39,.88);
  border-radius: 8px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
}
.viewer-loading,
.viewer-error {
  inset: 20px auto auto 50%;
  transform: translateX(-50%);
  z-index: 2;
}
.annotation-tools {
  background: #25272c;
  border-left: 1px solid rgba(255,255,255,.08);
  padding: 8px 6px;
}
.tool-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.annotation-tools button.active {
  background: #2563eb;
  border-color: #60a5fa;
}
.color-input {
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 0;
  border-radius: 7px;
  background: transparent;
}
.mobile-only {
  display: none;
}
:global(body.pdf-reader-fullscreen) {
  overflow: hidden;
}
:global(body.pdf-reader-fullscreen nav),
:global(body.pdf-reader-fullscreen footer),
:global(body.pdf-reader-fullscreen .bottom-nav),
:global(body.pdf-reader-fullscreen .floating-whatsapp),
:global(body.pdf-reader-fullscreen .contents-hero),
:global(body.pdf-reader-fullscreen .branches-strip),
:global(body.pdf-reader-fullscreen .contents-sidebar),
:global(body.pdf-reader-fullscreen .viewer-header) {
  display: none !important;
}
@media (max-width: 768px) {
  .pdf-learning-viewer {
    height: 76vh;
    min-height: 520px;
    border-radius: 10px;
  }
  .pdf-learning-viewer.fullscreen {
    height: 100vh;
  }
  .mobile-only {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .pdf-toolbar.collapsed .secondary-row {
    display: none;
  }
  .secondary-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: center;
  }
  .secondary-row .search-box {
    grid-column: 1 / -1;
  }
  .toolbar-row {
    min-height: 44px;
    padding: 5px 7px;
    gap: 4px;
  }
  .main-row {
    display: grid;
    grid-template-columns: 32px 32px minmax(64px, 1fr) 32px minmax(32px, auto) 32px;
  }
  .main-row .toolbar-spacer {
    display: none;
  }
  .main-row .save-state {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .secondary-row .icon-btn,
  .secondary-row .text-btn,
  .main-row .icon-btn {
    width: 100%;
    min-width: 0;
    padding: 0 4px;
    font-size: 11px;
    height: 32px;
  }
  .main-row .page-control input {
    width: 48px;
    height: 30px;
  }
  .search-box {
    width: 100%;
  }
  .search-box input {
    min-width: 0;
    width: 100%;
  }
  .search-box .mini-btn {
    flex: 0 0 auto;
  }
  .pdf-body {
    grid-template-columns: 1fr;
  }
  .pdf-scroll {
    padding-bottom: 76px;
  }
  .thumbs-panel {
    display: none;
    position: absolute;
    inset: 0 auto 0 0;
    width: 116px;
    z-index: 10020;
    transform: none;
  }
  .thumbs-panel.open {
    display: block;
  }
  .annotation-tools {
    position: fixed;
    left: 10px;
    right: 10px;
    bottom: var(--pdf-mobile-safe-bottom, calc(78px + env(safe-area-inset-bottom, 0px)));
    z-index: 10025;
    padding: 0;
    border: 0;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    pointer-events: none;
  }
  .tool-list {
    display: grid;
    grid-template-columns: repeat(4, 44px);
    gap: 8px;
    max-width: min(calc(100vw - 20px), 232px);
    max-height: var(--pdf-mobile-tool-sheet-max-height, 220px);
    overflow: visible;
    padding: 10px;
    margin-bottom: 10px;
    border-radius: 14px;
    background: #25272c;
    box-shadow: 0 10px 30px rgba(0,0,0,.3);
    border: 1px solid rgba(255,255,255,.1);
    opacity: 0;
    transform: translateY(12px) scale(.96);
    transform-origin: bottom right;
    pointer-events: none;
    transition: opacity .16s ease, transform .16s ease;
  }
  .annotation-tools.open .tool-list {
    opacity: 1;
    transform: translateY(0) scale(1);
    pointer-events: auto;
  }
  .annotation-tools .tool-list button,
  .annotation-tools .color-input {
    width: 44px;
    height: 44px;
    min-width: 44px;
  }
  .annotation-tools .tool-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 999px;
    background: #2563eb;
    color: #fff;
    border-color: rgba(255,255,255,.22);
    box-shadow: 0 12px 28px rgba(37,99,235,.38);
    overflow: visible;
    pointer-events: auto;
  }
  .annotation-tools .tool-toggle .pi {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 20px;
    line-height: 1;
    opacity: 1;
    width: 1em;
    height: 1em;
  }
  .floating-thumbs {
    position: sticky;
    top: 10px;
    left: 10px;
    z-index: 15;
    width: 42px;
  }
  .page-shell {
    padding: 8px;
  }
}
@media (max-width: 768px) and (orientation: landscape) {
  .pdf-learning-viewer {
    height: 100vh;
    min-height: 340px;
  }
  .annotation-tools {
    left: auto;
    width: min(232px, 48vw);
  }
  .tool-list {
    grid-template-columns: repeat(4, 40px);
    max-width: min(calc(100vw - 20px), 216px);
    max-height: var(--pdf-mobile-tool-sheet-max-height, 176px);
  }
  .annotation-tools .tool-list button,
  .annotation-tools .color-input {
    width: 40px;
    height: 40px;
    min-width: 40px;
  }
}
</style>
